/**
 * The accessible companions of an organization's brand color.
 *
 * ── Why this exists ─────────────────────────────────────────────────
 *
 * An org picks `primary_colour` on the Settings page, and the app
 * paints buttons with it and writes white on top. That is a coin
 * flip. Measured on the TrailsIQ default amber (#dd9b36): white on it
 * is **2.38:1**, against the 4.5:1 WCAG AA asks for — so every
 * primary button in the product, on both surfaces, had label text a
 * reader with ordinary middle-aged eyes has to lean in for. The same
 * hex used as TEXT on white is 2.38:1 as well, which covered the
 * accent links and the sidebar logo.
 *
 * ── Why the LABEL is not simply flipped to black ────────────────────
 *
 * That was the first answer here, and it was wrong in the field. A
 * customer running Stowe's orange (#e45419) got black labels, because
 * white scores 3.76 on it and near-black scores 4.71, so
 * best-of-the-two picked black. The report was the obvious one:
 * *"lots of the buttons now get a black font but it's much harder to
 * read than when they had white."*
 *
 * They were right, and the ratios were not lying — they were
 * answering a narrower question than the one that matters. WCAG 2.x
 * relative luminance is known to misjudge saturated mid-tones: it
 * weights green heavily and under-penalises dark ink on a strong
 * orange or red, so a pair that clears 4.5 on paper can still read as
 * mud. A 4.71 that looks worse than a 3.76 is the formula's edge, not
 * the reader's fault.
 *
 * So the button moves instead of the label. Darkening #e45419 by
 * **10%** lets white clear at 4.52 — a change most people would not
 * notice beside the original, against a label change everybody
 * noticed immediately.
 *
 * The brand color itself is untouched: `--org-primary` still paints
 * borders, dots and marks, which carry no text and need only 3:1.
 * Only the ground under a LABEL moves, and only as far as it must.
 *
 * ── The three derived values ────────────────────────────────────────
 *
 * `btn`  — the ground for a control that carries a LABEL. The brand
 *          color, darkened only as far as white needs to clear AA on
 *          it, and not at all when white already does.
 * `on`   — the ink to write on `btn`. White, in every case where the
 *          brand color can reach white at all.
 * `ink`  — the brand color as TEXT on a light background: the same
 *          hue, walked toward black until it clears 4.5:1 on white.
 *          Hue is preserved, so it still reads as their color.
 * `lift` — the same walked toward white until it clears 4.5:1 on the
 *          field app's dark sidebar. A brand color chosen to look
 *          good on paper is usually too dark to be a highlight there.
 *
 * ── Derived, never configured ───────────────────────────────────────
 *
 * There is deliberately no setting for these. An operator picking a
 * brand color cannot be asked to also pick a legible ink for it, and
 * an org that never answers would keep the failing pair — which is
 * the state this replaces. Same argument as every other derived
 * value in this app: a caller cannot forget what it never sets.
 */

import {
    AA_TEXT, INK_BLACK, contrast, inkOn, parseHex, toHex, walkToward,
} from './contrast.js';

export const DEFAULT_PRIMARY = '#dd9b36';
export const DEFAULT_PRIMARY_DARK = '#aa7624';

/** The field app's sidebar ground, which `lift` is measured against. */
export const FIELD_SIDEBAR = '#064e3b';

/**
 * The DARKEST light ground the accent is asked to sit on — a gray-100
 * chip. Cards are white, pages are gray-50, chips are gray-100, and
 * an ink solved against the lightest of those fails on the other two.
 *
 * Measured: solved against white, the default amber's ink lands at
 * 4.64 on white and 4.44 on the gray-50 a help page uses; solved
 * against gray-50 it still misses gray-100 for a yellow brand at
 * 4.27. Solving against the darkest ground costs one or two steps of
 * saturation and passes on all three, which is the trade the ink
 * exists to make.
 */
export const LIGHT_GROUND = '#f3f4f6';

/**
 * The full set for one brand color.
 *
 * @param {string|null|undefined} colour  The org's primary_colour.
 * @returns {{primary: string, dark: string, on: string, ink: string, lift: string}}
 */
/**
 * How far toward black a colour has to move before WHITE reads on it,
 * as a fraction — 0 when it already does, null when even black does
 * not get there (which no real colour reaches).
 */
const darkeningForWhite = (hex) => {
    const from = parseHex(hex);
    if (!from) return null;
    for (let i = 0; i <= 20; i++) {
        const t = i / 20;
        const step = toHex([from[0] * (1 - t), from[1] * (1 - t), from[2] * (1 - t)]);
        if (contrast('#ffffff', step) >= AA_TEXT) return t;
    }

    return null;
};

/**
 * The most a brand colour may be darkened to keep a white label.
 *
 * Beyond about a third of the way to black a colour stops being the
 * one somebody picked — a mid yellow becomes olive. Colours needing
 * more than this are LIGHT ones, where dark ink is the natural look
 * anyway and nobody reads a black-on-yellow button as a mistake.
 *
 * Measured over all 4,096 web-safe colours: 1,476 need no darkening
 * at all, and the ones past this line are the pale end of the range.
 */
export const MAX_BUTTON_DARKEN = 0.35;

export const orgColorSet = (colour) => {
    const primary = parseHex(colour) ? colour.toLowerCase() : DEFAULT_PRIMARY;
    const rgb = parseHex(primary);

    // The label stays white unless the colour is light enough that
    // white cannot be reached without destroying it.
    const shift = darkeningForWhite(primary);
    const keepsWhite = shift !== null && shift <= MAX_BUTTON_DARKEN;
    const btn = keepsWhite
        ? toHex([rgb[0] * (1 - shift), rgb[1] * (1 - shift), rgb[2] * (1 - shift)])
        : primary;
    const on = keepsWhite ? '#ffffff' : inkOn(primary);

    // Hover is derived from the BUTTON, not from the brand colour:
    // taking it from the original would make some buttons get lighter
    // on hover, which reads as the press not registering.
    const btnRgb = parseHex(btn);
    const dark = (primary === DEFAULT_PRIMARY && btn === primary)
        ? DEFAULT_PRIMARY_DARK
        : toHex([btnRgb[0] - 38, btnRgb[1] - 38, btnRgb[2] - 38]);

    return {
        primary,
        btn,
        dark,
        on,
        // Derived against the page GROUND, not against white.
        //
        // Measured: an ink solved for #ffffff lands at 4.64 there and
        // 4.44 on the gray-50 a card or a help page sits on — passing
        // where it was checked and failing where it renders. The app
        // has more gray-50 than white, so the ground is what it has
        // to clear; white then comes free with headroom.
        ink: walkToward(primary, INK_BLACK, LIGHT_GROUND, AA_TEXT),
        lift: walkToward(primary, '#ffffff', FIELD_SIDEBAR, AA_TEXT),
    };
};

/**
 * Stamp the set onto <html>, where app.css reads it.
 *
 * Both surfaces call this — the dashboard layout on every org switch,
 * the field app once at launch. It used to be inline in the layout and
 * the field app did not do it at all, so the phone painted the
 * TrailsIQ amber for every customer regardless of their brand.
 */
export const applyOrgColor = (colour, doc = typeof document === 'undefined' ? null : document) => {
    if (!doc) return null;
    const set = orgColorSet(colour);
    const root = doc.documentElement;
    root.style.setProperty('--org-primary', set.primary);
    root.style.setProperty('--org-primary-btn', set.btn);
    root.style.setProperty('--org-primary-dark', set.dark);
    root.style.setProperty('--org-primary-on', set.on);
    root.style.setProperty('--org-primary-ink', set.ink);
    root.style.setProperty('--org-primary-lift', set.lift);

    return set;
};
